import { sanitizePublicProviderText } from "@cmmd-forge/shared/publicProviderCopy";

const GENERIC_LOCAL_ENVIRONMENT_LABELS = new Set(["local", "local environment"]);

const INTERNAL_DETAIL_PATTERNS = [
  /(?:^|\s)\/(?:Users|var|private|tmp|project|repo|home)\/[^\s'"<>)]*/i,
  /(?:^|\s)[A-Za-z]:\\[^\s'"<>)]*/,
] as const;

function normalizeDisplayLabel(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : null;
}

export function resolveProductEnvironmentLabel(input: {
  readonly isPrimary: boolean;
  readonly runtimeLabel?: string | null;
  readonly savedLabel?: string | null;
}): string {
  const runtimeLabel = normalizeDisplayLabel(input.runtimeLabel);
  const savedLabel = normalizeDisplayLabel(input.savedLabel);

  if (input.isPrimary) {
    const preferredLocalLabel = [runtimeLabel, savedLabel].find((label) => {
      if (!label) return false;
      return !GENERIC_LOCAL_ENVIRONMENT_LABELS.has(label.toLowerCase());
    });
    return preferredLocalLabel ?? "This device";
  }

  return runtimeLabel ?? savedLabel ?? "Workspace";
}

export function formatProductProjectLabel(value: string | null | undefined): string {
  return normalizeDisplayLabel(value) ?? "Project";
}

export function formatProductWorkspacePath(
  path: string | null | undefined,
  mode: "safe" | "full" = "safe",
): string {
  const trimmed = normalizeDisplayLabel(path);
  if (!trimmed) return "Local checkout";
  if (mode === "full") return trimmed;

  const segments = trimmed.replaceAll("\\", "/").split("/").filter(Boolean);
  return segments.at(-1) ?? "Local checkout";
}

export function formatProductWorktreeTooltip(label: string | null | undefined): string {
  const resolvedLabel = normalizeDisplayLabel(label) ?? "Worktree";
  return `${resolvedLabel} checkout`;
}

export function sanitizePublicUiError(
  error: unknown,
  fallback = "Something went wrong. Try again.",
): string {
  const message =
    error instanceof Error ? error.message : typeof error === "string" ? error : String(error);
  const sanitizedProviderText = sanitizePublicProviderText(message, { fallback });
  if (!sanitizedProviderText) return fallback;
  if (INTERNAL_DETAIL_PATTERNS.some((pattern) => pattern.test(sanitizedProviderText))) {
    return fallback;
  }
  return sanitizedProviderText;
}
